import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  DollarSign,
  Users,
  FileText,
  Receipt,
  TrendingUp,
  Calendar,
  Shield,
  AlertCircle,
  Trash2,
  Edit,
  Mail,
  Phone,
  MapPin,
  Key,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  RefreshCw,
  Settings
} from 'lucide-react';
import { UserProfile } from '@/shared/types';
import { Button, Badge, Avatar } from '@/shared/components/ui';
import { useUserStats } from '@/features/admin/hooks/useUserStats';
import { useUserActivity } from '@/features/admin/hooks/useUserActivity';
import { adminService } from '@/shared/services/realData/admin';
import AdminUsersSkeleton from '@/features/admin/components/AdminUsersSkeleton';

interface UserDetailViewProps {
  onInitiateAction: (type: 'delete' | 'reset_password' | 'toggle_company' | 'change_plan' | null, user: UserProfile) => void;
}

/**
 * UserDetailView - Detailed view of a single user
 * Displays comprehensive user information including:
 * - Profile details (name, email, role, contact info)
 * - Subscription details (plan, billing, payment methods)
 * - Account statistics (clients, quotes, invoices, revenue)
 * - Activity timeline (recent quotes, invoices, payments)
 * - Quick actions (reset password, change plan, delete, toggle status)
 */
const UserDetailView: React.FC<UserDetailViewProps> = ({ onInitiateAction }) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user statistics
  const { stats, loading: statsLoading, error: statsError } = useUserStats(userId);
  // Fetch user activity
  const { activity, loading: activityLoading, error: activityError } = useUserActivity(userId);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all users and find the matching one
        const users = await adminService.adminGetAllUsers();
        const foundUser = users.find(u => u.id === userId);

        if (!foundUser) {
          setError('User not found');
        } else {
          setUser(foundUser);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    if (!user) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: user.currency || 'USD'
    }).format(amount);
  };

  const handleAction = (actionType: 'delete' | 'reset_password' | 'toggle_company' | 'change_plan') => {
    if (user) {
      onInitiateAction(actionType, user);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <AdminUsersSkeleton />
      </div>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded border border-gray-200 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">User Not Found</h2>
          <p className="text-gray-600 mb-5 text-sm">{error || 'The user you are looking for does not exist.'}</p>
          <Link to="/admin/users">
            <Button variant="primary">
              <ArrowLeft size={14} className="mr-2" />
              Back to Users
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/users">
            <Button variant="outline" size="sm">
              <ArrowLeft size={14} className="mr-2" />
              Back to Users
            </Button>
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">User Details</h1>
            <p className="text-xs text-gray-500">Comprehensive view of user profile and activity</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAction('reset_password')}
            className="text-amber-600 hover:text-amber-800 border-amber-300 hover:border-amber-400"
          >
            <Key size={14} className="mr-2" />
            Reset Password
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAction('change_plan')}
            className="text-blue-600 hover:text-blue-800 border-blue-300 hover:border-blue-400"
          >
            <CreditCard size={14} className="mr-2" />
            Change Plan
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAction('toggle_company')}
            className="text-purple-600 hover:text-purple-800 border-purple-300 hover:border-purple-400"
          >
            <Shield size={14} className="mr-2" />
            {user.subscription.planId === 'company' ? 'Remove Company' : 'Make Company'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAction('delete')}
            className="text-red-600 hover:text-red-800 border-red-300 hover:border-red-400"
          >
            <Trash2 size={14} className="mr-2" />
            Delete Account
          </Button>
        </div>
      </div>

      {/* User Profile Header */}
      <div className="bg-gradient-to-r from-gray-50 to-white p-5 rounded border border-gray-200">
        <div className="flex items-start gap-4">
          <Avatar
            src={user.avatarUrl || undefined}
            alt={user.name}
            size="xl"
            className="ring-2 ring-white shadow"
          />
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{user.name}</h2>
                <p className="text-gray-600 flex items-center gap-2 mt-1 text-sm">
                  <Mail size={14} />
                  {user.email}
                </p>
              </div>
              <Badge variant={user.role === 'admin' ? 'primary' : 'default'} size="md">
                <Shield size={14} className="mr-1.5" />
                {user.role.toUpperCase()}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600">
              {user.businessPhone && (
                <span className="flex items-center gap-1.5">
                  <Phone size={14} />
                  {user.businessPhone}
                </span>
              )}
              {user.businessAddress && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} />
                  {user.businessAddress}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                Joined {formatDate(user.joinedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Account Statistics */}
      <div>
        <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-primary-600" />
          Account Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-primary-600" size={16} />
              <span className="text-xs text-gray-400 uppercase font-medium">Total</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {statsLoading ? '...' : stats.totalClients}
            </p>
            <p className="text-xs text-gray-500 mt-1">Clients</p>
          </div>
          <div className="bg-white p-4 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FileText className="text-blue-600" size={16} />
              <span className="text-xs text-gray-400 uppercase font-medium">Total</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {statsLoading ? '...' : stats.totalQuotes}
            </p>
            <p className="text-xs text-gray-500 mt-1">Quotes Sent</p>
          </div>
          <div className="bg-white p-4 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Receipt className="text-green-600" size={16} />
              <span className="text-xs text-gray-400 uppercase font-medium">Total</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {statsLoading ? '...' : stats.totalInvoices}
            </p>
            <p className="text-xs text-gray-500 mt-1">Invoices Created</p>
          </div>
          <div className="bg-white p-4 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-emerald-600" size={16} />
              <span className="text-xs text-gray-400 uppercase font-medium">Total</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {statsLoading ? '...' : formatCurrency(stats.totalRevenue)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Revenue Generated</p>
          </div>
          <div className="bg-white p-4 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-purple-600" size={16} />
              <span className="text-xs text-gray-400 uppercase font-medium">Total</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {statsLoading ? '...' : formatCurrency(stats.totalPayments)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Payments Received</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Subscription Details */}
        <div className="bg-white p-5 rounded border border-gray-200">
          <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
            <Building2 size={16} className="text-primary-600" />
            Subscription Details
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <p className="text-xs text-gray-500">Current Plan</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-base font-semibold text-gray-900 capitalize">
                    {user.subscription.planId}
                  </p>
                  <Badge variant={user.subscription.status === 'active' ? 'success' : 'error'} size="xs">
                    {user.subscription.status}
                  </Badge>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction('change_plan')}
              >
                <Edit size={12} className="mr-1" />
                Change
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">Billing Period</p>
                <p className="text-sm font-medium text-gray-900 capitalize mt-1">
                  {user.subscription.billingPeriod}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">Next Billing</p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {formatDate(user.subscription.nextBillingDate)}
                </p>
              </div>
            </div>
            {user.subscription.paymentMethod && (
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">Payment Method</p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {user.subscription.paymentMethod}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Business Profile */}
        <div className="bg-white p-5 rounded border border-gray-200">
          <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
            <Building2 size={16} className="text-primary-600" />
            Business Profile
          </h3>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Business Name</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {user.businessName || 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Business Email</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {user.businessEmail || user.email}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Business Phone</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {user.businessPhone || 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Business Address</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {user.businessAddress || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Settings */}
      <div className="bg-white p-5 rounded border border-gray-200">
        <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
          <Settings size={16} className="text-primary-600" />
          Account Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 bg-gray-50 rounded">
            <p className="text-xs text-gray-500">Currency</p>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {user.currency}
            </p>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <p className="text-xs text-gray-500">Country</p>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {user.country}
            </p>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <p className="text-xs text-gray-500">Template</p>
            <p className="text-sm font-medium text-gray-900 capitalize mt-1">
              {user.template}
            </p>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <p className="text-xs text-gray-500">Onboarding</p>
            <div className="flex items-center gap-2 mt-1">
              {user.hasCompletedOnboarding ? (
                <CheckCircle size={16} className="text-green-600" />
              ) : (
                <XCircle size={16} className="text-red-600" />
              )}
              <p className="text-sm font-medium text-gray-900">
                {user.hasCompletedOnboarding ? 'Completed' : 'Pending'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div>
        <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
          <Activity size={16} className="text-primary-600" />
          Recent Activity
        </h3>
        {activityLoading ? (
          <div className="bg-white rounded border border-gray-200 p-6 text-center text-gray-500">
            <RefreshCw className="mx-auto h-6 w-6 animate-spin mb-2" />
            Loading activity...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Recent Quotes */}
            <div className="bg-white p-4 rounded border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                  <FileText size={14} className="text-blue-600" />
                  Recent Quotes
                </h4>
                <Badge variant="default" size="xs">{activity.recentQuotes.length}</Badge>
              </div>
              {activity.recentQuotes.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">No quotes yet</p>
              ) : (
                <div className="space-y-2">
                  {activity.recentQuotes.slice(0, 5).map((quote) => (
                    <div key={quote.id} className="p-3 bg-gray-50 rounded border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{quote.title}</p>
                          <p className="text-xs text-gray-500">#{quote.quote_number}</p>
                        </div>
                        <span className="ml-2 text-xs font-medium text-gray-700">
                          {formatCurrency(parseFloat(quote.total_amount.toString()))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <Badge variant={quote.status === 'accepted' ? 'success' : 'default'} size="xs">
                          {quote.status}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatDate(quote.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Invoices */}
            <div className="bg-white p-4 rounded border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                  <Receipt size={14} className="text-green-600" />
                  Recent Invoices
                </h4>
                <Badge variant="default" size="xs">{activity.recentInvoices.length}</Badge>
              </div>
              {activity.recentInvoices.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">No invoices yet</p>
              ) : (
                <div className="space-y-2">
                  {activity.recentInvoices.slice(0, 5).map((invoice) => (
                    <div key={invoice.id} className="p-3 bg-gray-50 rounded border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{invoice.title}</p>
                          <p className="text-xs text-gray-500">#{invoice.invoice_number}</p>
                        </div>
                        <span className="ml-2 text-xs font-medium text-gray-700">
                          {formatCurrency(parseFloat(invoice.total_amount.toString()))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <Badge
                          variant={
                            invoice.status === 'paid' ? 'success' :
                            invoice.status === 'overdue' ? 'error' : 'default'
                          }
                          size="xs"
                        >
                          {invoice.status}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatDate(invoice.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Payments */}
            <div className="bg-white p-4 rounded border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                  <DollarSign size={14} className="text-emerald-600" />
                  Recent Payments
                </h4>
                <Badge variant="default" size="xs">{activity.recentPayments.length}</Badge>
              </div>
              {activity.recentPayments.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">No payments yet</p>
              ) : (
                <div className="space-y-2">
                  {activity.recentPayments.slice(0, 5).map((payment) => (
                    <div key={payment.id} className="p-3 bg-gray-50 rounded border border-gray-100">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">Payment Received</p>
                        </div>
                        <span className="text-xs font-medium text-green-600">
                          {formatCurrency(parseFloat(payment.amount.toString()))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <Badge variant={payment.status === 'completed' ? 'success' : 'default'} size="xs">
                          {payment.status}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatDate(payment.payment_date)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions Summary */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-4 rounded border border-primary-200">
        <h3 className="font-semibold text-gray-900 text-sm mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            variant="outline"
            className="bg-white hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 text-sm"
            onClick={() => handleAction('reset_password')}
          >
            <Key size={14} className="mr-2" />
            Reset Password
          </Button>
          <Button
            variant="outline"
            className="bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 text-sm"
            onClick={() => handleAction('change_plan')}
          >
            <CreditCard size={14} className="mr-2" />
            Change Plan
          </Button>
          <Button
            variant="outline"
            className="bg-white hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 text-sm"
            onClick={() => handleAction('toggle_company')}
          >
            <Shield size={14} className="mr-2" />
            Toggle Company
          </Button>
          <Button
            variant="outline"
            className="bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 text-sm"
            onClick={() => handleAction('delete')}
          >
            <Trash2 size={14} className="mr-2" />
            Delete Account
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailView;
